/**
 * Chip — the design system's compact selectable/removable token.
 *
 * Ported 1:1 from `tw-components.jsx`; `cx` and `Icon` are imported because the
 * app vendors each upstream component as its own module.
 */
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";

export function Chip({
  children,
  icon,
  selected = false,
  selectable = false,
  onRemove,
  onClick,
  style = {},
  className = "",
  ...rest
}) {
  const isStatic = !selectable && !onClick && !onRemove;

  return (
    <button
      type="button"
      style={style}
      aria-pressed={selectable ? selected : undefined}
      onClick={onClick}
      className={cx(
        "dnd-chip inline-flex h-[34px] select-none items-center gap-[var(--space-2)] rounded-[var(--radius-pill)] border-[1.5px] pl-[var(--space-4)] font-sans text-sm font-medium",
        isStatic ? "pr-[var(--space-3)]" : "cursor-pointer pr-[var(--space-4)]",
        "[transition:background_var(--duration-fast)_var(--ease-standard),border-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),transform_var(--duration-base)_var(--ease-spring)]",
        "active:scale-[0.95] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]",
        selected
          ? "border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700"
          : "border-line bg-card text-ink hover:border-blue-400 hover:bg-blue-50",
        className
      )}
      {...rest}
    >
      {selectable && selected && (
        <Icon className="ml-[-2px]" name="check" size={16} weight={500} />
      )}
      {icon &&
        !(selectable && selected) &&
        (typeof icon === "string" ? (
          <Icon name={icon} size={16} weight={400} />
        ) : (
          icon
        ))}
      <span>{children}</span>
      {onRemove && (
        <span
          className={cx(
            "mr-[-6px] inline-flex rounded-full p-[2px] [transition:background_var(--duration-fast)]",
            selected
              ? "hover:bg-[rgba(255,255,255,0.22)]"
              : "hover:bg-[rgba(0,0,0,0.12)]"
          )}
          role="button"
          aria-label="Remove"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(event);
          }}
        >
          <Icon name="close" size={15} weight={500} />
        </span>
      )}
    </button>
  );
}
